import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../contexts/CartContext';
import {
  readMemberFromStorage,
  clearMemberSession,
  JAVILAND_MEMBER_AUTH_EVENT,
  getMemberFirstName
} from '../utils/memberSession';
import './Header.css';

const MOBILE_HEADER_BREAKPOINT = 768;

const Header = ({ onCommunityClick, onEcommerceClick }) => {
  const { t } = useTranslation();
  const { itemCount, toggleCart } = useCart();
  const [memberUser, setMemberUser] = useState(() => readMemberFromStorage());
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showTiendaMenu, setShowTiendaMenu] = useState(false);
  const [showServiciosMenu, setShowServiciosMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileServicios, setShowMobileServicios] = useState(false);
  const [showMobileTienda, setShowMobileTienda] = useState(false);
  const hideTimeoutRef = useRef(null);
  const menuTimeoutRef = useRef(null);
  const serviciosTimeoutRef = useRef(null);

  useEffect(() => {
    const isNarrow = () => window.innerWidth <= MOBILE_HEADER_BREAKPOINT;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (isNarrow()) {
        setIsVisible(true);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
        hideTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
        }, 2000);
      }

      if (currentScrollY > 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      setLastScrollY(currentScrollY);
    };

    const handleResize = () => {
      if (isNarrow()) {
        setIsVisible(true);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [lastScrollY]);

  useEffect(() => {
    const syncMember = () => setMemberUser(readMemberFromStorage());
    syncMember();
    window.addEventListener('popstate', syncMember);
    window.addEventListener(JAVILAND_MEMBER_AUTH_EVENT, syncMember);
    window.addEventListener('storage', syncMember);
    return () => {
      window.removeEventListener('popstate', syncMember);
      window.removeEventListener(JAVILAND_MEMBER_AUTH_EVENT, syncMember);
      window.removeEventListener('storage', syncMember);
    };
  }, []);

  const memberFirstName = memberUser ? getMemberFirstName(memberUser) : '';
  const memberShortLabel =
    memberFirstName ||
    (memberUser?.email ? String(memberUser.email).split('@')[0] : '') ||
    '';

  const handleMemberLogout = () => {
    clearMemberSession();
    setMemberUser(null);
    setShowMobileMenu(false);
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const navigateToPage = (path) => {
    // Usar window.history.pushState para navegación sin recarga
    window.history.pushState({}, '', path);
    // Disparar evento personalizado para que AppRouter lo detecte
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const goCommunity = () => {
    if (onCommunityClick) onCommunityClick();
  };

  return (
    <>
    <header className={`header ${isScrolled ? 'scrolled' : ''} ${!isVisible ? 'hidden' : ''}`}>
      <div className="header-content">
        {/* Logo a la izquierda */}
        <div className="logo-container" onClick={() => navigateToPage('/')}>
          <img 
            src="/images/Logo_Javiland_Largo.png" 
            alt="Javiland Logo" 
            className="logo-image"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <span 
            className="logo-text fallback" 
            style={{ display: 'none' }}
          >
            JAVILAND
          </span>
        </div>
        
        {/* Menú hamburguesa para móvil */}
        <button 
          className="mobile-menu-toggle"
          onClick={() => {
            setShowMobileMenu(!showMobileMenu);
            setShowMobileServicios(false);
            setShowMobileTienda(false);
          }}
          aria-label="Toggle menu"
        >
          <i className={`fas ${showMobileMenu ? 'fa-times' : 'fa-bars'}`}></i>
        </button>
        
        {/* Menú centrado (desktop) */}
        <nav className="nav-menu-container">
          <ul className="nav-menu">
            <li><button className="nav-link" onClick={() => navigateToPage('/')}>{t('header.home')}</button></li>
            
            {/* Menú desplegable de Tienda */}
            <li 
              className="nav-item-dropdown"
              onMouseEnter={() => {
                if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
                setShowTiendaMenu(true);
              }}
              onMouseLeave={() => {
                menuTimeoutRef.current = setTimeout(() => setShowTiendaMenu(false), 200);
              }}
            >
              <button className="nav-link" onClick={() => navigateToPage('/tienda')}>
                {t('header.store')} <i className="fas fa-chevron-down"></i>
              </button>
              {showTiendaMenu && (
                <div className="dropdown-menu">
                  <button className="dropdown-item" onClick={() => { navigateToPage('/tienda'); setShowTiendaMenu(false); }}>
                    {t('header.storeClothing')}
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateToPage('/tienda?category=productos-naturales'); setShowTiendaMenu(false); }}>
                    {t('header.naturalProducts')}
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateToPage('/tienda?category=instrumentos-musicales'); setShowTiendaMenu(false); }}>
                    {t('header.music')}
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateToPage('/tienda?category=joyeria-artesanias'); setShowTiendaMenu(false); }}>
                    {t('header.jewelry')}
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateToPage('/tienda?category=bienestar'); setShowTiendaMenu(false); }}>
                    {t('header.wellness')}
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateToPage('/tienda?category=tecnologia'); setShowTiendaMenu(false); }}>
                    {t('header.technology')}
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateToPage('/tienda?category=jardineria'); setShowTiendaMenu(false); }}>
                    {t('header.gardening')}
                  </button>
                  <button
                    type="button"
                    className="dropdown-item dropdown-item--sale"
                    onClick={() => { navigateToPage('/tienda?category=sale'); setShowTiendaMenu(false); }}
                  >
                    {t('header.sale')}
                  </button>
                </div>
              )}
            </li>
            
            {/* Menú desplegable de Servicios */}
            <li 
              className="nav-item-dropdown"
              onMouseEnter={() => {
                if (serviciosTimeoutRef.current) clearTimeout(serviciosTimeoutRef.current);
                setShowServiciosMenu(true);
              }}
              onMouseLeave={() => {
                serviciosTimeoutRef.current = setTimeout(() => setShowServiciosMenu(false), 200);
              }}
            >
              <button className="nav-link" onClick={() => navigateToPage('/servicios')}>
                {t('header.services')} <i className="fas fa-chevron-down"></i>
              </button>
              {showServiciosMenu && (
                <div className="dropdown-menu">
                  <button className="dropdown-item" onClick={() => { navigateToPage('/bienestar'); setShowServiciosMenu(false); }}>
                    {t('header.wellness')}
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateToPage('/conecta-tu-camino'); setShowServiciosMenu(false); }}>
                    {t('header.connectPath')}
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateToPage('/voluntariado'); setShowServiciosMenu(false); }}>
                    {t('header.volunteering')}
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateToPage('/conoce-colombia'); setShowServiciosMenu(false); }}>
                    {t('header.conoceColombia')}
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateToPage('/growth-partner'); setShowServiciosMenu(false); }}>
                    {t('header.growthPartner')}
                  </button>
                </div>
              )}
            </li>
            
            <li><button className="nav-link" onClick={() => navigateToPage('/noticias')}>{t('header.news')}</button></li>
          </ul>
        </nav>
        
        {/* Menú móvil desplegable - Home, Tienda, Servicios (expandible), Contacto */}
        {showMobileMenu && (
          <nav className="mobile-menu">
            {memberUser && (
              <div className="mobile-member-banner">
                <button
                  type="button"
                  className="mobile-nav-link mobile-member-to-perfil-btn"
                  onClick={() => { navigateToPage('/perfil'); setShowMobileMenu(false); }}
                  title={memberUser.email || ''}
                >
                  {memberUser.avatar_data ? (
                    <img src={memberUser.avatar_data} alt="" className="mobile-member-btn-avatar" />
                  ) : (
                    <span className="mobile-member-btn-avatar mobile-member-btn-avatar--placeholder" aria-hidden>
                      <i className="fas fa-user" />
                    </span>
                  )}
                  <span className="mobile-member-btn-name">{memberShortLabel}</span>
                </button>
                <button type="button" className="mobile-nav-link mobile-member-logout" onClick={handleMemberLogout}>
                  {t('header.memberLogout')}
                </button>
              </div>
            )}
            <button className="mobile-nav-link" onClick={() => { navigateToPage('/'); setShowMobileMenu(false); }}>
              {t('header.home')}
            </button>
            <div className="mobile-nav-group">
              <button
                type="button"
                className="mobile-nav-link mobile-nav-expandable"
                onClick={() => setShowMobileTienda(!showMobileTienda)}
                aria-expanded={showMobileTienda}
              >
                {t('header.store')}{' '}
                <i className={`fas fa-chevron-down mobile-nav-chevron ${showMobileTienda ? 'open' : ''}`} />
              </button>
              {showMobileTienda && (
                <div className="mobile-nav-sublinks">
                  <button
                    type="button"
                    className="mobile-nav-sublink"
                    onClick={() => {
                      navigateToPage('/tienda');
                      setShowMobileMenu(false);
                      setShowMobileTienda(false);
                    }}
                  >
                    {t('header.storeClothing')}
                  </button>
                  <button
                    type="button"
                    className="mobile-nav-sublink"
                    onClick={() => {
                      navigateToPage('/tienda?category=productos-naturales');
                      setShowMobileMenu(false);
                      setShowMobileTienda(false);
                    }}
                  >
                    {t('header.naturalProducts')}
                  </button>
                  <button
                    type="button"
                    className="mobile-nav-sublink"
                    onClick={() => {
                      navigateToPage('/tienda?category=instrumentos-musicales');
                      setShowMobileMenu(false);
                      setShowMobileTienda(false);
                    }}
                  >
                    {t('header.music')}
                  </button>
                  <button
                    type="button"
                    className="mobile-nav-sublink"
                    onClick={() => {
                      navigateToPage('/tienda?category=joyeria-artesanias');
                      setShowMobileMenu(false);
                      setShowMobileTienda(false);
                    }}
                  >
                    {t('header.jewelry')}
                  </button>
                  <button
                    type="button"
                    className="mobile-nav-sublink"
                    onClick={() => {
                      navigateToPage('/tienda?category=bienestar');
                      setShowMobileMenu(false);
                      setShowMobileTienda(false);
                    }}
                  >
                    {t('header.wellness')}
                  </button>
                  <button
                    type="button"
                    className="mobile-nav-sublink"
                    onClick={() => {
                      navigateToPage('/tienda?category=tecnologia');
                      setShowMobileMenu(false);
                      setShowMobileTienda(false);
                    }}
                  >
                    {t('header.technology')}
                  </button>
                  <button
                    type="button"
                    className="mobile-nav-sublink"
                    onClick={() => {
                      navigateToPage('/tienda?category=jardineria');
                      setShowMobileMenu(false);
                      setShowMobileTienda(false);
                    }}
                  >
                    {t('header.gardening')}
                  </button>
                  <button
                    type="button"
                    className="mobile-nav-sublink mobile-nav-sublink--sale"
                    onClick={() => {
                      navigateToPage('/tienda?category=sale');
                      setShowMobileMenu(false);
                      setShowMobileTienda(false);
                    }}
                  >
                    {t('header.sale')}
                  </button>
                </div>
              )}
            </div>
            <div className="mobile-nav-group">
              <button 
                className="mobile-nav-link mobile-nav-expandable" 
                onClick={() => setShowMobileServicios(!showMobileServicios)}
                aria-expanded={showMobileServicios}
              >
                {t('header.services')} <i className={`fas fa-chevron-down mobile-nav-chevron ${showMobileServicios ? 'open' : ''}`}></i>
              </button>
              {showMobileServicios && (
                <div className="mobile-nav-sublinks">
                  <button
                    type="button"
                    className="mobile-nav-sublink"
                    onClick={() => {
                      navigateToPage('/servicios');
                      setShowMobileMenu(false);
                      setShowMobileServicios(false);
                    }}
                  >
                    {t('header.servicesLanding')}
                  </button>
                  <button className="mobile-nav-sublink" onClick={() => { navigateToPage('/bienestar'); setShowMobileMenu(false); setShowMobileServicios(false); }}>
                    {t('header.wellness')}
                  </button>
                  <button className="mobile-nav-sublink" onClick={() => { navigateToPage('/conecta-tu-camino'); setShowMobileMenu(false); setShowMobileServicios(false); }}>
                    {t('header.connectPath')}
                  </button>
                  <button className="mobile-nav-sublink" onClick={() => { navigateToPage('/voluntariado'); setShowMobileMenu(false); setShowMobileServicios(false); }}>
                    {t('header.volunteering')}
                  </button>
                  <button className="mobile-nav-sublink" onClick={() => { navigateToPage('/conoce-colombia'); setShowMobileMenu(false); setShowMobileServicios(false); }}>
                    {t('header.conoceColombia')}
                  </button>
                  <button className="mobile-nav-sublink" onClick={() => { navigateToPage('/growth-partner'); setShowMobileMenu(false); setShowMobileServicios(false); }}>
                    {t('header.growthPartner')}
                  </button>
                </div>
              )}
            </div>
            <button className="mobile-nav-link" onClick={() => { navigateToPage('/noticias'); setShowMobileMenu(false); }}>
              {t('header.news')}
            </button>
            {!memberUser && (
              <button
                className="mobile-nav-link mobile-user-action"
                onClick={() => { goCommunity(); setShowMobileMenu(false); }}
              >
                <i className="fas fa-leaf" /> {t('header.communityShort')}
              </button>
            )}
          </nav>
        )}
        
        {/* Acciones: sesión miembro (desktop), carrito, comunidad si no hay sesión */}
        <div className="header-actions">
          {memberUser && (
            <div className="header-member-session desktop-only" aria-live="polite">
              <button
                type="button"
                className="header-member-to-perfil"
                onClick={() => navigateToPage('/perfil')}
                title={memberUser.email || ''}
                aria-label={`${t('header.memberProfile')}: ${memberShortLabel}`}
              >
                {memberUser.avatar_data ? (
                  <img src={memberUser.avatar_data} alt="" className="header-member-btn-avatar" />
                ) : (
                  <span className="header-member-btn-avatar header-member-btn-avatar--placeholder" aria-hidden>
                    <i className="fas fa-user" />
                  </span>
                )}
                <span className="header-member-btn-name">{memberShortLabel}</span>
              </button>
              <button type="button" className="header-member-logout-btn" onClick={handleMemberLogout}>
                {t('header.memberLogout')}
              </button>
            </div>
          )}
          <button
            type="button"
            className="cart-icon"
            onClick={toggleCart}
            title={t('header.cart')}
            aria-label={t('header.cart')}
          >
            <i className="fas fa-shopping-cart"></i>
            {itemCount > 0 && (
              <span className="cart-badge">{itemCount}</span>
            )}
          </button>
          {!memberUser && (
            <button
              type="button"
              className="user-action-button logo-only desktop-only header-community-btn"
              onClick={goCommunity}
              title="Panel especial — comunidad"
            >
              <i className="fas fa-leaf" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </header>
    </>
  );
};

export default Header;
